import * as R from 'remeda';
import { timeUtility } from 'utility';

function extractIntervalDateIds<T extends Omit<Data.DateItem, 'hour'>>(args: {
  items: T[];
}): string[] {
  const { items } = args;
  // construct interval
  const first = R.first(items);
  const last = R.last(items);

  // Not interested in empty list
  if (!R.isDefined(first)) return [];
  if (!R.isDefined(last)) return [];

  const first_date_item = timeUtility.factory<Time.Day>({
    ...first,
    type: 'day',
  });

  const last_date_item = timeUtility.factory<Time.Day>({
    ...last,
    type: 'day',
  });

  const dateIds: string[] = [];

  let currentDate = first_date_item;
  dateIds.push(currentDate.id);

  while (currentDate.id !== last_date_item.id) {
    currentDate = timeUtility.step({
      timeDate: currentDate,
      step: 'next',
      stepSize: 'day',
    });

    dateIds.push(currentDate.id);
  }

  return dateIds;
}

// every resolution; activityId / subZoneId must have a placeholder list of the sum to calculate ma
function constructSumsByGroup(args: {
  resolutions: string[];
  interval: number;
}) {
  const { interval, resolutions } = args;
  // const resolutions = R.map(R.keys(byResolutionKey);

  function constructEmptyList(interval: number) {
    const sum: (number | null)[] = [];
    R.times(interval, () => sum.push(null));
    return sum;
  }

  const sumsByGroup = R.pipe(
    resolutions,
    R.mapToObj((resolution) => [
      String(resolution),
      constructEmptyList(interval),
    ]),
  );

  return { sumsByGroup };
}

/**
 * This function will take a domain and decorate it with moving value.
 * callback will be called for every date in interval start to end.
 * Will result in more items than original, and all values will h
 */

function decorateDomainWithMovingValue<
  T extends Omit<Data.DateItem, 'hour'>,
>(args: {
  domain: NormalizedDomain<T>;
  valueKey: keyof T; // spentTime, noPickingRows
  groupKey: keyof T; // activityTypeId, activityId, zoneId, subZoneId - right now it should be closest to data.
  parentGroupKey: keyof T;
  // will be called for every date in interval
  callback: (args: {
    item: T | null;
    movingSum: number | null;
    dateId: string;
    groupId: string;
    parentGroupId: string;
  }) => void;
}) {
  const INTERVAL = 21;

  const { callback, domain, groupKey, valueKey, parentGroupKey } = args;

  const items = R.pipe(
    domain.allIds,
    R.map((id) => domain.byId[id] as T),
    R.sortBy((item) =>
      timeUtility.generateDayId(item.year, item.month, item.dayOfMonth),
    ),
  );

  const intervalDateIds = extractIntervalDateIds({ items });
  const itemByDateId = R.pipe<T[], Record<string, T[]>>(
    items,
    R.groupBy((item) =>
      timeUtility.generateDayId(item.year, item.month, item.dayOfMonth),
    ),
  );
  const itemByGroup = R.pipe<T[], Record<string, T[]>>(
    items,
    R.groupBy((item) => item[groupKey] as string),
  );

  const parentGroupByGroup = R.pipe(
    itemByGroup,
    R.mapValues((items) =>
      R.pipe(
        items,
        R.groupBy((item) => item[parentGroupKey] as string),
        R.keys,
        R.first(),
      ),
    ),
  );
  let { sumsByGroup } = constructSumsByGroup({
    resolutions: R.keys(itemByGroup),
    interval: INTERVAL,
  });

  for (
    let index = INTERVAL - 1; // No correct moving values if we don't have days >= INTERVAL
    index < intervalDateIds.length;
    index++
  ) {
    const dateId = intervalDateIds[index]!;

    const dateItemByGroup = R.pipe<T[], Record<string, T[]>, Record<string, T>>(
      itemByDateId[dateId] ?? [],
      R.groupBy((item) => item[groupKey] as string),
      R.mapValues((dateItems, groupId) => R.first(dateItems)!), // will always have 1 item
    );

    sumsByGroup = R.pipe(
      sumsByGroup,
      R.mapValues((sums, groupId) => {
        const dateItem = dateItemByGroup[groupId];
        const parentGroupId = parentGroupByGroup[groupId]!;

        const movingSum = R.reduce<number | null, number | null>(
          sums,
          (acc, item) => {
            if (!R.isDefined(acc)) return item;
            if (!R.isDefined(item)) return acc;

            return item + acc;
          },
          null,
        );

        callback({
          item: dateItem ?? null,
          dateId,
          groupId,
          parentGroupId,
          movingSum,
        });

        const metric = R.isDefined(dateItem)
          ? (dateItem[valueKey] as number)
          : null;

        // shift array 1 step
        const [, tail] = R.splitAt(sums, 1);
        return [...tail, metric];
      }),
    );
  }
}

export { decorateDomainWithMovingValue };
